import { useQuery, UseQueryResult, useQueryClient } from 'react-query';

import { useAuth } from 'src/hooks/useAuth';
import { getReInviteUser } from 'src/utils/urls';
import { showErrorToast, showSuccessToast } from 'src/utils/ToastNotification';

type ApiErrorItem = {
  Code: string;
  FieldName: string;
  Message: string;
};

type TResult = {
  Errors: ApiErrorItem[];
  ErrorId: string;
  IsSuccess: boolean;
  StatusCode: number;
  Data?: boolean;
};

type TError = Error;

type TVaribles = {
  token: string;
  tokenType: string;
  id: string;
};

async function fetchReinviteUser({
  token,
  tokenType,
  id,
}: TVaribles): Promise<TResult> {
  const response = await fetch(getReInviteUser(id), {
    method: 'PUT',
    headers: {
      Authorization: `${tokenType} ${token}`,
    },
  });

  let data: TResult | null = null;

  try {
    const text = await response.text();
    data = text ? JSON.parse(text) : null;
  } catch (e) {
    console.error('JSON parse failed:', e);
  }

  if (!data) {
    throw new Error('Invalid server response');
  }

  return data;
}

function useGetReInvite(id: string): UseQueryResult<TResult, TError> {
  const { token, tokenType } = useAuth();
  const queryClient = useQueryClient();

  return useQuery(
    ['get_user_re_invite', id],
    async () => {
      const result = await fetchReinviteUser({ token, tokenType, id });

      if (!result.IsSuccess) {
        const errorMessage =
          result.Errors?.map(err => err.Message).join(', ') ||
          'Failed to send re-invite';

        showErrorToast({ message: errorMessage });
        return result;
      }

      queryClient.invalidateQueries('get_users_list');

      showSuccessToast({
        message: 'Re-invitation sent successfully.',
      });

      return result;
    },
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      enabled: false,
    },
  );
}

export { useGetReInvite };
